import React, { useEffect, useState } from "react";
import { useCashbookStore, useTrialBalanceStore } from "../../../config/stores";
import { Card, Space, Alert, Spin, Form, Typography } from "antd";
import { find, isEmpty, map } from "lodash";
import AppButton from "../../../components/common/AppButton";
import AppInput from "../../../components/common/AppInput";
import {
  AiOutlineFileExcel,
  AiOutlineReload,
  AiOutlineFilePdf,
} from "react-icons/ai";
import dayjs from "dayjs";
import generateTrialBalancePdf from "../../../config/services/trialBalancePdf";
import TrialBalanceTable from "./TrialBalanceTable";

const { Title } = Typography;

const ProjectTrialBalance = () => {
  const cashbookStore = useCashbookStore();
  const trialBalanceStore = useTrialBalanceStore();

  const [form] = Form.useForm();
  const [startDate, setStartDate] = useState(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState(dayjs());
  const [projectId, setProjectId] = useState(null);
  const [trialBalance, setTrialBalance] = useState(null);
  const [totals, setTotals] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  const getDefaultContext = () => {
    const startDateValue = startDate
      ? dayjs(startDate).format("YYYY-MM-DD")
      : dayjs().startOf("month").format("YYYY-MM-DD");
    const endDateValue = endDate
      ? dayjs(endDate).format("YYYY-MM-DD")
      : dayjs().format("YYYY-MM-DD");
    return { startDate: startDateValue, endDate: endDateValue, projectId };
  };

  useEffect(() => {
    const defaultContext = getDefaultContext();
    form.setFieldsValue({
      startDate: dayjs(defaultContext.startDate),
      endDate: dayjs(defaultContext.endDate),
      projectId: projectId || undefined,
    });
  }, []);

  useEffect(() => {
    const context = getDefaultContext();
    if (!context.projectId) return;

    const existingData = find(
      trialBalanceStore.projectTrialBalance,
      (records) =>
        records?.startDate === context.startDate &&
        records?.endDate === context.endDate &&
        records?.projectId === context.projectId
    );

    if (existingData) {
      const data = existingData?.data;
      const trialBalanceData = data?.data?.trialBalance || data?.trialBalance;
      const totalsData = data?.data?.totals || data?.totals;

      if (trialBalanceData || totalsData) {
        setTrialBalance({ trialBalance: trialBalanceData, totals: totalsData });
        setTotals(totalsData);
      } else {
        setTrialBalance(data);
        setTotals(data?.totals);
      }
      setHasLoaded(true);
    }
  }, [trialBalanceStore.projectTrialBalance, startDate, endDate, projectId]);

  const handleGetTrialBalance = () => {
    const context = getDefaultContext();
    if (!context.projectId) return;

    setHasLoaded(false);
    trialBalanceStore.getProjectTrialBalance(context).finally(() => {
      setHasLoaded(true);
    });
  };

  const reloadTrialBalance = () => {
    handleGetTrialBalance();
  };

  const downloadTrialBalance = () => {
    const context = getDefaultContext();
    if (!context.projectId) return;
    const fileName = `PROJECT_TRIAL_BALANCE_${context.projectId}_${context.startDate}_TO_${context.endDate}.xlsx`;
    trialBalanceStore.downloadProjectTrialBalance(context, fileName);
  };

  const downloadTrialBalancePdf = () => {
    if (trialBalance && totals) {
      const context = getDefaultContext();
      generateTrialBalancePdf(trialBalance, context.endDate);
    }
  };

  const isLoading =
    trialBalanceStore.gettingProjectTrialBalance && !hasLoaded;
  const hasError = !isEmpty(trialBalanceStore.projectTrialBalanceError);
  const hasData = trialBalance?.trialBalance?.length > 0;
  const currentStartDate = startDate
    ? dayjs(startDate).format("YYYY-MM-DD")
    : dayjs().startOf("month").format("YYYY-MM-DD");
  const currentEndDate = endDate
    ? dayjs(endDate).format("YYYY-MM-DD")
    : dayjs().format("YYYY-MM-DD");

  return (
    <div>
      <Card
        bordered={false}
        title={
          <div>
            <Title level={4} style={{ margin: 0 }}>
              PROJECT TRIAL BALANCE
            </Title>
            <div
              style={{ fontSize: "14px", color: "#666", marginTop: "4px" }}
            >
              Period: {currentStartDate} to {currentEndDate}
            </div>
          </div>
        }
        extra={
          <Space>
            <AppButton
              text="RELOAD"
              loadingText="RELOADING..."
              loading={trialBalanceStore.gettingProjectTrialBalance}
              iconBefore={<AiOutlineReload />}
              onClick={reloadTrialBalance}
              disabled={!projectId}
            />
            <AppButton
              text="DOWNLOAD EXCEL"
              danger
              type="dashed"
              loadingText="DOWNLOADING..."
              iconBefore={<AiOutlineFileExcel />}
              loading={trialBalanceStore.downloadingProjectTrialBalance}
              onClick={downloadTrialBalance}
              disabled={!hasData}
            />
            <AppButton
              text="DOWNLOAD PDF"
              type="primary"
              loadingText="GENERATING..."
              iconBefore={<AiOutlineFilePdf />}
              onClick={downloadTrialBalancePdf}
              disabled={!hasData}
            />
          </Space>
        }
      >
        <Form
          form={form}
          layout="inline"
          onFinish={handleGetTrialBalance}
          style={{ marginBottom: "16px" }}
        >
          <AppInput
            name="projectId"
            type="select"
            label="Project"
            rules={[{ required: true, message: "Please select a project" }]}
            options={map(cashbookStore.metadata?.projects, (project) => ({
              value: project.id,
              label: project.name,
            }))}
            inputAttributes={{
              onChange: (value) => setProjectId(value),
            }}
          />
          <AppInput
            name="startDate"
            label="Start Date"
            type="date"
            rules={[{ required: true, message: "Please select a start date" }]}
            inputAttributes={{
              onChange: (date) => {
                setStartDate(date);
              },
            }}
          />
          <AppInput
            name="endDate"
            label="End Date"
            type="date"
            rules={[{ required: true, message: "Please select an end date" }]}
            inputAttributes={{
              onChange: (date) => {
                setEndDate(date);
              },
              disabledDate: (current) =>
                current && startDate && current.isBefore(dayjs(startDate), "day"),
            }}
          />
          <Form.Item>
            <AppButton
              text="GET PROJECT TRIAL BALANCE"
              loadingText="Loading..."
              loading={trialBalanceStore.gettingProjectTrialBalance}
              htmlType="submit"
            />
          </Form.Item>
        </Form>
        {!projectId ? (
          <Alert
            type="info"
            showIcon
            message="Please select a project and date to view the trial balance."
          />
        ) : isLoading ? (
          <div style={{ textAlign: "center", padding: "50px" }}>
            <Spin size="large" />
            <div style={{ marginTop: "16px" }}>
              Loading project trial balance...
            </div>
          </div>
        ) : hasError ? (
          <Alert
            type="error"
            showIcon
            message="Error Loading Project Trial Balance"
          />
        ) : !hasData ? (
          <Alert
            type="info"
            showIcon
            message="No Trial Balance Data"
            description={`No trial balance data available for the period ${currentStartDate} to ${currentEndDate} for the selected project.`}
          />
        ) : (
          <TrialBalanceTable trialBalanceData={trialBalance} totals={totals} />
        )}
      </Card>
    </div>
  );
};

export default ProjectTrialBalance;

